import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { num } from '../lib/format.js';
import { ErrorState, LoadingState, Panel, StatusBadge } from '../components/ui.js';

type Source = 'eshop' | 'se' | 'web' | 'watchdog';

interface Proposal {
  code: string;
  name: string;
  what: string;
  why: string;
  sources: Source[];
  definition: Record<string, unknown>;
  popis: string;
  saved: boolean;
}

const SOURCE_LABEL: Record<Source, string> = {
  eshop: 'E-shop',
  se: 'SmartEmailing',
  web: 'Web (snippet)',
  watchdog: 'Hlídací psi',
};

/**
 * Připravené návrhy segmentů bez ohledu na datum: u každého je vidět, co v něm je, odkud data jsou a proč, a kolik
 * kontaktů odpovídá. Vytvoření je opakovatelné (stálý kód). Všechny mají povinné marketingové výluky.
 */
export function SegmentProposals({ canEdit }: { canEdit: boolean }): JSX.Element {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['segment-proposals'],
    queryFn: () => api.get<{ items: Proposal[] }>('/api/segments/proposals'),
  });
  const [counts, setCounts] = useState<Record<string, { members: number; matching: number }>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [made, setMade] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);

  const count = async (p: Proposal): Promise<void> => {
    setBusy(p.code);
    try {
      const r = await api.post<{ members: number; matching: number }>('/api/segments/preview', {
        definition: p.definition,
        light: true,
      });
      setCounts((c) => ({ ...c, [p.code]: { members: r.members, matching: r.matching } }));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const countAll = async (): Promise<void> => {
    for (const p of query.data?.items ?? []) {
      if (!counts[p.code]) await count(p);
    }
  };
  const create = useMutation({
    mutationFn: (p: Proposal) =>
      api.put<{ members: number }>(`/api/segments/${encodeURIComponent(p.code)}`, {
        code: p.code,
        name: p.name,
        description: `${p.what} ${p.why}`.slice(0, 500),
        definition: p.definition,
        enabled: true,
        pushToProvider: false,
      }),
    onSuccess: (r, p) => {
      setError(null);
      setMade((m) => ({ ...m, [p.code]: r.members }));
      void client.invalidateQueries({ queryKey: ['segments'] });
      void client.invalidateQueries({ queryKey: ['segment-proposals'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Panel
      title="Návrhy segmentů"
      subtitle="Hotové skupiny podle toho, co o lidech víme: kdo koupil, kdo hlídá, kdo je jen ve SmartEmailingu, kdo chodí na web. Nezávisí na datu: „budoucí závod“ se počítá z kalendáře vždy aktuálně. Každá má povinné výluky (souhlas s marketingem, bez odhlášených, blokovaných a kontrolní skupiny)."
      action={
        <button
          type="button"
          className="btn"
          disabled={busy !== null}
          onClick={() => void countAll()}
        >
          Spočítat všechny
        </button>
      }
    >
      <details>
        <summary>Odkud se berou data a co je „smí dostat marketing“</summary>
        <div className="stack">
          <ul>
            <li>
              <b>E-shop:</b> zákazníci, objednávky, závody (termíny, zrušení), hlídací psi a hlavně{' '}
              <b>souhlas s marketingem</b> (události `consent.changed` a import historie).
            </li>
            <li>
              <b>SmartEmailing:</b> členství v seznamech (zájem, hlídání, kupující, uvítací slevy),
              odhlášení, blokace a tvrdé odskoky, čas posledního otevření a kliku. <b>Souhlas</b> ze
              SmartEmailingu se do hubu dostane jen tam, kde správce při importu zapnul vybrané
              seznamy a účely; jinak ze SE přichází jen odhlášení a blokace.
            </li>
            <li>
              <b>Web (snippet):</b> návštěvy a klíčové akce rozpoznaných lidí se souhlasem s
              měřením.
            </li>
            <li>
              <b>Dopočítává hub:</b> aktivita (aktivní, zvolna, spící), „budoucí závod“ z kalendáře,
              kontrolní skupina.
            </li>
          </ul>
          <p className="note">
            <b>Smí dostat marketing</b> = má souhlas s marketingem <b>a</b> není odhlášený ve
            SmartEmailingu, nemá blokovanou adresu ani tvrdý odskok <b>a</b> není v kontrolní
            skupině. Kdo souhlas nemá (třeba lidé z historie dev e-shopu), se v počtech neobjeví. U
            každé karty je proto vidět, kolik kontaktů podmínkám odpovídá a kolik z nich smí
            marketing.
          </p>
        </div>
      </details>
      {query.isLoading ? <LoadingState what="návrhy" /> : null}
      {query.isError ? (
        <ErrorState title="Návrhy se nepodařilo načíst" error={query.error} />
      ) : null}
      {error ? <ErrorState title="Akce se nepovedla" error={new Error(error)} /> : null}
      <div className="grid2" data-testid="segment-proposals">
        {(query.data?.items ?? []).map((p) => {
          const c = counts[p.code];
          return (
            <div key={p.code} className="card-choice" style={{ cursor: 'default' }}>
              <b>{p.name}</b>
              <p className="note">{p.what}</p>
              <p className="note">
                <i>Proč:</i> {p.why}
              </p>
              <p>
                {p.sources.map((s) => (
                  <StatusBadge key={s} tone="neu">
                    {SOURCE_LABEL[s]}
                  </StatusBadge>
                ))}
              </p>
              {c ? (
                <p data-testid={`count-${p.code}`}>
                  Odpovídá <b>{num(c.matching)}</b>, smí marketing <b>{num(c.members)}</b>
                  {c.matching > c.members ? (
                    <small className="muted">
                      {' '}
                      ({num(c.matching - c.members)} bez souhlasu nebo vyloučeno)
                    </small>
                  ) : null}
                </p>
              ) : null}
              <div className="row">
                <button
                  type="button"
                  className="btn"
                  disabled={busy !== null}
                  onClick={() => void count(p)}
                >
                  {busy === p.code ? 'Počítám…' : 'Spočítat'}
                </button>
                {canEdit ? (
                  <button
                    type="button"
                    className="btn pri"
                    disabled={create.isPending}
                    onClick={() => create.mutate(p)}
                  >
                    {p.saved || made[p.code] !== undefined
                      ? 'Aktualizovat segment'
                      : 'Vytvořit segment'}
                  </button>
                ) : null}
              </div>
              {made[p.code] !== undefined ? (
                <StatusBadge tone="ok" icon="check">
                  uloženo, {num(made[p.code] ?? 0)} kontaktů
                </StatusBadge>
              ) : p.saved ? (
                <StatusBadge tone="ok" icon="check">
                  už existuje
                </StatusBadge>
              ) : null}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
